/**
 * Schema Validation Utilities for SchemaTable Component
 * Implements FR-019: Schema validation with error display and console logging
 */

import { validatorLogger } from './logger.js';

/**
 * Supported data types for columns
 */
export const SUPPORTED_DATA_TYPES = [
  'string',
  'number', 
  'boolean',
  'date',
  'currency'
];

/**
 * Supported Bootstrap button variants for actions
 */
export const SUPPORTED_BUTTON_VARIANTS = [
  'primary',
  'secondary', 
  'success',
  'warning',
  'danger',
  'info',
  'light', 
  'dark'
];

/**
 * Validate complete table schema
 * @param {Object} schema - The schema object to validate
 * @returns {Object} Validation result with { isValid, errors, warnings }
 */
export function validateSchema(schema) {
  const result = {
    isValid: true,
    errors: [],
    warnings: []
  };

  // Check if schema exists and is an object
  if (!schema || typeof schema !== 'object') {
    result.isValid = false;
    result.errors.push('Schema must be a valid object');
    return result;
  }

  // Validate columns array (required)
  if (!schema.columns) {
    result.isValid = false;
    result.errors.push('Schema must have a "columns" property');
  } else if (!Array.isArray(schema.columns)) {
    result.isValid = false;
    result.errors.push('Schema columns must be an array');
  } else if (schema.columns.length === 0) {
    result.isValid = false;
    result.errors.push('Schema must have at least one column definition');
  } else {
    // Validate each column
    const columnValidation = validateColumns(schema.columns);
    result.errors.push(...columnValidation.errors);
    result.warnings.push(...columnValidation.warnings);
    if (!columnValidation.isValid) {
      result.isValid = false;
    }
  }

  // Validate optional properties
  if (schema.actions) {
    const actionValidation = validateActions(schema.actions);
    result.errors.push(...actionValidation.errors);
    result.warnings.push(...actionValidation.warnings);
    if (!actionValidation.isValid) {
      result.isValid = false;
    }
  }

  if (schema.defaultSortColumn) {
    const sortValidation = validateSortConfiguration(schema.defaultSortColumn, schema.defaultSortDirection, schema.columns);
    result.errors.push(...sortValidation.errors);
    result.warnings.push(...sortValidation.warnings);
    if (!sortValidation.isValid) {
      result.isValid = false;
    }
  }

  return result;
}

/**
 * Validate columns array
 * @param {Array} columns - Array of column definitions
 * @returns {Object} Validation result
 */
export function validateColumns(columns) {
  const result = {
    isValid: true,
    errors: [],
    warnings: []
  };

  const columnNames = new Set();

  columns.forEach((column, index) => {
    const columnValidation = validateSingleColumn(column, index);
    result.errors.push(...columnValidation.errors);
    result.warnings.push(...columnValidation.warnings);
    if (!columnValidation.isValid) {
      result.isValid = false;
    }

    // Check for duplicate column names
    if (column.name) {
      if (columnNames.has(column.name)) {
        result.isValid = false;
        result.errors.push(`Duplicate column name "${column.name}" at index ${index}`);
      } else {
        columnNames.add(column.name);
      }
    }
  });

  return result;
}

/**
 * Validate a single column definition
 * @param {Object} column - Column definition object
 * @param {number} index - Column index for error reporting
 * @returns {Object} Validation result
 */
export function validateSingleColumn(column, index) {
  const result = {
    isValid: true,
    errors: [],
    warnings: []
  };

  const columnRef = `column at index ${index}`;

  // Required properties
  if (!column.name || typeof column.name !== 'string' || column.name.trim() === '') {
    result.isValid = false;
    result.errors.push(`${columnRef} must have a non-empty "name" property`);
  }

  if (!column.label || typeof column.label !== 'string' || column.label.trim() === '') {
    result.isValid = false;
    result.errors.push(`${columnRef} must have a non-empty "label" property`);
  }

  if (!column.dataPath || typeof column.dataPath !== 'string' || column.dataPath.trim() === '') {
    result.isValid = false;
    result.errors.push(`${columnRef} must have a non-empty "dataPath" property`);
  }

  if (!column.dataType || !SUPPORTED_DATA_TYPES.includes(column.dataType)) {
    result.isValid = false;
    result.errors.push(`${columnRef} must have a valid "dataType" (${SUPPORTED_DATA_TYPES.join(', ')})`);
  }

  // Optional properties validation
  if (column.formatter && typeof column.formatter !== 'function') {
    result.warnings.push(`${columnRef} formatter should be a function`);
  }

  if (column.align && !['left', 'center', 'right'].includes(column.align)) {
    result.warnings.push(`${columnRef} align should be 'left', 'center', or 'right'`);
  }

  if (column.sortable && typeof column.sortable !== 'boolean') {
    result.warnings.push(`${columnRef} sortable should be a boolean`);
  }

  return result;
}

/**
 * Validate actions array
 * @param {Array} actions - Array of action definitions
 * @returns {Object} Validation result
 */
export function validateActions(actions) {
  const result = {
    isValid: true,
    errors: [],
    warnings: []
  };

  if (!Array.isArray(actions)) {
    result.isValid = false;
    result.errors.push('Actions must be an array');
    return result;
  }

  const eventNames = new Set();

  actions.forEach((action, index) => {
    const actionValidation = validateSingleAction(action, index);
    result.errors.push(...actionValidation.errors);
    result.warnings.push(...actionValidation.warnings);
    if (!actionValidation.isValid) {
      result.isValid = false;
    }

    // Check for duplicate event names
    if (action.event) {
      if (eventNames.has(action.event)) {
        result.warnings.push(`Duplicate action event "${action.event}" at index ${index}`);
      } else {
        eventNames.add(action.event);
      }
    }
  });

  return result;
}

/**
 * Validate a single action definition
 * @param {Object} action - Action definition object
 * @param {number} index - Action index for error reporting
 * @returns {Object} Validation result
 */
export function validateSingleAction(action, index) {
  const result = {
    isValid: true,
    errors: [],
    warnings: []
  };

  const actionRef = `action at index ${index}`;

  // Required properties
  if (action.type !== 'button') {
    result.isValid = false;
    result.errors.push(`${actionRef} type must be 'button'`);
  }

  if (!action.label || typeof action.label !== 'string' || action.label.trim() === '') {
    result.isValid = false;
    result.errors.push(`${actionRef} must have a non-empty "label" property`);
  }

  if (!action.event || typeof action.event !== 'string' || action.event.trim() === '') {
    result.isValid = false;
    result.errors.push(`${actionRef} must have a non-empty "event" property`);
  }

  // Optional properties validation
  if (action.variant && !SUPPORTED_BUTTON_VARIANTS.includes(action.variant)) {
    result.warnings.push(`${actionRef} variant should be one of: ${SUPPORTED_BUTTON_VARIANTS.join(', ')}`);
  }

  if (action.condition && typeof action.condition !== 'function') {
    result.warnings.push(`${actionRef} condition should be a function`);
  }

  if (action.disabled && typeof action.disabled !== 'boolean' && typeof action.disabled !== 'function') {
    result.warnings.push(`${actionRef} disabled should be a boolean or function`);
  }

  return result;
}

/**
 * Validate sort configuration
 * @param {string} defaultSortColumn - Default sort column name
 * @param {string} defaultSortDirection - Default sort direction
 * @param {Array} columns - Array of column definitions
 * @returns {Object} Validation result
 */
export function validateSortConfiguration(defaultSortColumn, defaultSortDirection, columns) {
  const result = {
    isValid: true,
    errors: [],
    warnings: []
  };

  // Check if default sort column exists in columns
  if (defaultSortColumn) {
    const columnExists = columns && columns.some(col => col.name === defaultSortColumn);
    if (!columnExists) {
      result.isValid = false;
      result.errors.push(`Default sort column "${defaultSortColumn}" does not exist in columns`);
    }
  }

  // Check default sort direction
  if (defaultSortDirection && !['asc', 'desc'].includes(defaultSortDirection)) {
    result.isValid = false;
    result.errors.push('Default sort direction must be "asc" or "desc"');
  }

  return result;
}

/**
 * Log validation errors and warnings to console for developer debugging
 * @param {Object} validationResult - Result from validateSchema()
 * @param {Object} schema - The original schema object
 */
export function logValidationDetails(validationResult, schema) {
  if (!validationResult.isValid || validationResult.warnings.length > 0) {
    validatorLogger.debug('🔍 Schema Validation Details');
    
    if (!validationResult.isValid) {
      validatorLogger.error('❌ Schema validation failed:');
      validationResult.errors.forEach(error => validatorLogger.error(`  • ${error}`));
    }
    
    if (validationResult.warnings.length > 0) {
      validatorLogger.warn('⚠️ Schema validation warnings:');
      validationResult.warnings.forEach(warning => validatorLogger.warn(`  • ${warning}`));
    }
    
    validatorLogger.debug('📋 Schema object:', schema);
  } else {
    validatorLogger.info('✅ Schema validation passed');
  }
}

/**
 * Create a detailed error message for display to users
 * @param {Object} validationResult - Result from validateSchema()
 * @returns {string} User-friendly error message
 */
export function createUserErrorMessage(validationResult) {
  if (validationResult.isValid) {
    return null;
  }

  const errorCount = validationResult.errors.length;
  const warningCount = validationResult.warnings.length;
  
  let message = `Schema validation failed with ${errorCount} error${errorCount !== 1 ? 's' : ''}`;
  
  if (warningCount > 0) {
    message += ` and ${warningCount} warning${warningCount !== 1 ? 's' : ''}`;
  }
  
  message += '. Check the browser console for detailed information.';
  
  return message;
}